import { useState } from "react";
import { Zap } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface Props {
  onSwitchToLogin: () => void;
}

export function SignupPage({ onSwitchToLogin }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "var(--color-bg-base)" }}
      >
        <div className="w-full max-w-sm text-center">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg mb-6 mx-auto"
            style={{ background: "var(--color-brand)" }}
          >
            <Zap size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <div
            className="rounded-2xl p-8"
            style={{
              background: "var(--color-bg-surface)",
              border: "1px solid var(--color-border-default)",
            }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "rgba(16, 185, 129, 0.12)" }}
            >
              <span style={{ color: "var(--color-success)", fontSize: "1.5rem" }}>✓</span>
            </div>
            <h2
              className="text-title font-bold mb-2"
              style={{ color: "var(--color-text-primary)" }}
            >
              Check your email
            </h2>
            <p className="text-caption" style={{ color: "var(--color-text-muted)" }}>
              We sent a confirmation link to <strong style={{ color: "var(--color-text-secondary)" }}>{email}</strong>. Click it to activate your account.
            </p>
          </div>
          <p
            className="text-center text-caption mt-4"
            style={{ color: "var(--color-text-muted)" }}
          >
            Already confirmed?{" "}
            <button
              onClick={onSwitchToLogin}
              className="font-medium transition-colors"
              style={{ color: "var(--color-accent)" }}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--color-bg-base)" }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg mb-3"
            style={{ background: "var(--color-brand)" }}
          >
            <Zap size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <h1
            className="text-headline font-extrabold tracking-tighter"
            style={{ color: "var(--color-text-primary)" }}
          >
            Stride
          </h1>
          <p className="text-caption mt-1" style={{ color: "var(--color-text-muted)" }}>
            Create your account
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-6"
          style={{
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-default)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="signup-email"
                className="text-caption font-medium"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                style={{
                  background: "var(--color-bg-raised)",
                  border: "1px solid var(--color-border-default)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="signup-password"
                className="text-caption font-medium"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Password
              </label>
              <input
                id="signup-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                style={{
                  background: "var(--color-bg-raised)",
                  border: "1px solid var(--color-border-default)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="signup-confirm"
                className="text-caption font-medium"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Confirm password
              </label>
              <input
                id="signup-confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 rounded-lg text-body transition-colors"
                style={{
                  background: "var(--color-bg-raised)",
                  border: "1px solid var(--color-border-default)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            {error && (
              <p
                className="text-caption px-3 py-2 rounded-lg"
                style={{
                  color: "#f87171",
                  background: "rgba(248, 113, 113, 0.1)",
                  border: "1px solid rgba(248, 113, 113, 0.2)",
                }}
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-caption font-semibold transition-opacity disabled:opacity-50"
              style={{
                background: "var(--color-accent)",
                color: "#fff",
              }}
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
        </div>

        {/* Switch to login */}
        <p
          className="text-center text-caption mt-4"
          style={{ color: "var(--color-text-muted)" }}
        >
          Already have an account?{" "}
          <button
            onClick={onSwitchToLogin}
            className="font-medium transition-colors"
            style={{ color: "var(--color-accent)" }}
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
